import { createElement, createRef, Component } from 'react';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Schema } from 'prosemirror-model';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap } from 'prosemirror-commands';
import ReactDOM from 'react-dom';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var schema = new Schema({
    nodes: {
        doc: {
            content: 'block+'
        },
        paragraph: {
            content: 'inline*',
            group: 'block',
            selectable: false,
            parseDOM: [{ tag: 'p' }],
            toDOM: function () {
                return ['p', 0];
            },
        },
        pmBlockquote: {
            content: 'paragraph+',
            group: 'block',
            defining: true,
            selectable: false,
            attrs: {
                class: { default: 'pm-blockquote' },
            },
            parseDOM: [{ tag: 'blockquote' }],
            toDOM: function (node) {
                return ['blockquote', node.attrs, 0];
            },
        },
        blockquote: {
            content: 'paragraph+',
            group: 'block',
            defining: true,
            selectable: false,
            parseDOM: [{ tag: 'blockquote' }],
            toDOM: function () {
                return ['blockquote', 0];
            },
        },
        text: {
            group: 'inline'
        },
    }
});

function createNewBlockQuote(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    var blockquote = state.schema.nodes.blockquote;
    var empty = blockquote.createAndFill();
    var endOfBlock = $from.end();
    if (empty && dispatch) {
        var tr = state.tr.insert(endOfBlock + 1, empty);
        dispatch(tr);
    }
    return false;
}
function createNewPmBlockQuote(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    var blockquote = state.schema.nodes.pmBlockquote;
    var empty = blockquote.createAndFill();
    var endOfBlock = $from.end();
    if (empty && dispatch) {
        var tr = state.tr.insert(endOfBlock + 1, empty);
        dispatch(tr);
    }
    return false;
}

var plugins = function () {
    var plugins = [];
    plugins.push(history());
    plugins.push(keymap(baseKeymap));
    plugins.push(keymap({
        'Ctrl-Alt-b': createNewBlockQuote,
        'Ctrl-Alt-p': createNewPmBlockQuote,
    }));
    return plugins;
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".blockquote {\n  box-sizing: border-box;\n  color: #6a737d;\n  padding: 0 1em;\n  border-left: 4px solid #dfe2e5;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0; }\n";
styleInject(css_248z);

// Copied from here https://gist.github.com/esmevane/7326b19e20a5670954b51ea8618d096d
// Here we have the (too simple) React component which
// we'll be rendering content into.
//
var BlockQuote = /** @class */ (function (_super) {
    __extends(BlockQuote, _super);
    function BlockQuote(props) {
        var _this = _super.call(this, props) || this;
        _this.hole = createRef();
        return _this;
    }
    // We'll put the content into what we render using
    // this function, which appends a given node to
    // a ref HTMLElement, if present.
    //
    BlockQuote.prototype.append = function (node) {
        if (this.hole) {
            this.hole.current.appendChild(node);
        }
    };
    BlockQuote.prototype.render = function () {
        // The styled components version is basically just a wrapper to do SCSS styling.
        // Questionable if it's even needed for such simple styling and because you can't clearly see the 
        // DOM structure from the code (hence making `& > ${Component}` selectors quite unintuitive) 
        return createElement("blockquote", { ref: this.hole, className: "blockquote" });
    };
    return BlockQuote;
}(Component));

// Copied from here https://gist.github.com/esmevane/7326b19e20a5670954b51ea8618d096d
// This class is our actual interactor for ProseMirror itself.
// It glues DOM rendering, React, and ProseMirror nodes together.
//
var BlockQuoteView = /** @class */ (function () {
    function BlockQuoteView(node, view, getPos, decorations) {
        var _this = this;
        // This is the least complex part.  Now we've put
        // all of our interlocking pieces behind refs and
        // instance properties, this becomes the callback
        // which performs the actual shell game.
        //
        this.putContentDomInRef = function () {
            _this.ref.current.append(_this.contentDOM);
        };
        this.attrs = node.attrs;
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.decorations = decorations;
        // We'll use this to access our Underlined component's 
        // instance methods.
        //
        this.ref = createRef();
        // Here, we'll provide a container to render React into.
        // Coincidentally, this is where ProseMirror will put its
        // generated contentDOM.  React will throw out that content
        // once rendered, and at the same time we'll append it into
        // the component tree, like a fancy shell game.  This isn't
        // obvious to the user, but would it be more obvious on an
        // expensive render?
        //
        this.dom = document.createElement('span');
        // Finally, we provide an element to render content into.
        // We will be moving this node around as we need to.
        //
        this.contentDOM = document.createElement('span');
        // Just example classes to help see the structure in the DOM
        this.dom.classList.add('node__dom');
        this.contentDOM.classList.add('node__content-dom');
        // Better way of doing this would be portals https://reactjs.org/docs/portals.html
        ReactDOM.render(createElement(BlockQuote, { ref: this.ref }), this.dom, this.putContentDomInRef);
    }
    BlockQuoteView.prototype.update = function (node) {
        return true;
    };
    // Required to not to leave the React nodes orphaned.
    BlockQuoteView.prototype.destroy = function () {
        ReactDOM.unmountComponentAtNode(this.dom);
    };
    return BlockQuoteView;
}());

var nodeViews = {
    blockquote: function (node, view, getPos, decorations) { return new BlockQuoteView(node, view, getPos, decorations); },
};

var css_248z$1 = "#minimal-editor {\n  border: 1px solid black; }\n  #minimal-editor > .ProseMirror {\n    min-height: 140px;\n    overflow-wrap: break-word;\n    outline: none;\n    padding: 10px;\n    white-space: pre-wrap; }\n  #minimal-editor .pm-blockquote {\n    box-sizing: border-box;\n    color: #2d82e1;\n    padding: 0 1em;\n    border-left: 4px solid #48a1fa;\n    margin: 0.2rem 0 0 0;\n    margin-right: 0; }\n";
styleInject(css_248z$1);

var Editor = /** @class */ (function (_super) {
    __extends(Editor, _super);
    function Editor(props) {
        var _this = _super.call(this, props) || this;
        _this.createEditorView = function (element) {
            if (element != null) {
                _this.editorView = new EditorView(element, {
                    nodeViews: nodeViews,
                    state: _this.editorState,
                });
            }
        };
        _this.editorState = EditorState.create({
            schema: schema,
            plugins: plugins(),
        });
        _this.editorRef = createRef();
        return _this;
    }
    Editor.prototype.componentDidMount = function () {
        this.createEditorView(this.editorRef.current);
        this.forceUpdate();
    };
    Editor.prototype.componentWillUnmount = function () {
        if (this.editorView) {
            this.editorView.destroy();
        }
    };
    Editor.prototype.shouldComponentUpdate = function () {
        return false;
    };
    Editor.prototype.render = function () {
        return createElement("div", { id: "minimal-editor", ref: this.editorRef });
    };
    return Editor;
}(Component));

export { Editor };
